export const ASSET_TYPE_OPTIONS = [
  { value: "forex", label: "Forex" },
  { value: "metals", label: "Metals" },
  { value: "indices", label: "Indices" },
  { value: "commodities", label: "Commodities" },
  { value: "crypto", label: "Crypto" },
  { value: "stock", label: "Stock" },
];

export const CURRENCY_OPTIONS = [
  "USD",
  "EUR",
  "GBP",
  "JPY",
  "CHF",
  "CAD",
  "AUD",
  "NZD",
  "NGN",
  "SEK",
  "NOK",
  "DKK",
  "SGD",
  "HKD",
  "ZAR",
  "MXN",
  "TRY",
  "PLN",
  "CZK",
  "HUF",
  "CNH",
  "CNY",
];

const FX_CURRENCIES = new Set(CURRENCY_OPTIONS);
const CRYPTO_BASES = new Set([
  "BTC",
  "ETH",
  "SOL",
  "BNB",
  "XRP",
  "ADA",
  "DOGE",
  "AVAX",
  "DOT",
  "LTC",
  "LINK",
  "TRX",
  "MATIC",
  "ATOM",
  "TON",
  "NEAR",
  "BCH",
  "UNI",
]);
const CRYPTO_QUOTES = ["USDT", "USDC", "USD", "EUR", "GBP", "JPY", "BTC", "ETH"];
const BROKER_SUFFIX_PATTERN = /^[A-Z0-9]{0,6}$/;

const KNOWN_SYMBOLS = {
  XAUUSD: { assetType: "metals", tradeCurrency: "USD", positionField: "lot_size", instrumentLabel: "Gold" },
  XAGUSD: { assetType: "metals", tradeCurrency: "USD", positionField: "lot_size", instrumentLabel: "Silver" },
  XPTUSD: { assetType: "metals", tradeCurrency: "USD", positionField: "lot_size", instrumentLabel: "Platinum" },
  XPDUSD: { assetType: "metals", tradeCurrency: "USD", positionField: "lot_size", instrumentLabel: "Palladium" },
  US100: { assetType: "indices", tradeCurrency: "USD", positionField: "lot_size", instrumentLabel: "US 100" },
  NAS100: { assetType: "indices", tradeCurrency: "USD", positionField: "lot_size", instrumentLabel: "NASDAQ 100" },
  USTEC: { assetType: "indices", tradeCurrency: "USD", positionField: "lot_size", instrumentLabel: "US Tech 100" },
  NDX100: { assetType: "indices", tradeCurrency: "USD", positionField: "lot_size", instrumentLabel: "NASDAQ 100" },
  US30: { assetType: "indices", tradeCurrency: "USD", positionField: "lot_size", instrumentLabel: "US 30" },
  WS30: { assetType: "indices", tradeCurrency: "USD", positionField: "lot_size", instrumentLabel: "Wall Street 30" },
  DJ30: { assetType: "indices", tradeCurrency: "USD", positionField: "lot_size", instrumentLabel: "Dow Jones 30" },
  SP500: { assetType: "indices", tradeCurrency: "USD", positionField: "lot_size", instrumentLabel: "S&P 500" },
  SPX500: { assetType: "indices", tradeCurrency: "USD", positionField: "lot_size", instrumentLabel: "S&P 500" },
  US500: { assetType: "indices", tradeCurrency: "USD", positionField: "lot_size", instrumentLabel: "US 500" },
  USOIL: { assetType: "commodities", tradeCurrency: "USD", positionField: "lot_size", instrumentLabel: "US Oil" },
  WTI: { assetType: "commodities", tradeCurrency: "USD", positionField: "lot_size", instrumentLabel: "WTI Crude" },
  XTIUSD: { assetType: "commodities", tradeCurrency: "USD", positionField: "lot_size", instrumentLabel: "US Oil" },
  UKOIL: { assetType: "commodities", tradeCurrency: "USD", positionField: "lot_size", instrumentLabel: "UK Oil" },
  BRENT: { assetType: "commodities", tradeCurrency: "USD", positionField: "lot_size", instrumentLabel: "Brent Crude" },
  XBRUSD: { assetType: "commodities", tradeCurrency: "USD", positionField: "lot_size", instrumentLabel: "Brent Crude" },
  NATGAS: { assetType: "commodities", tradeCurrency: "USD", positionField: "lot_size", instrumentLabel: "Natural Gas" },
};

function normalizeTradeCurrency(currency) {
  const normalized = String(currency || "").trim().toUpperCase();
  if (normalized === "USDT" || normalized === "USDC") {
    return "USD";
  }
  return normalized;
}

export function normalizeAssetSymbol(asset) {
  if (!asset) {
    return "";
  }

  return String(asset).trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function buildForexInference(symbol) {
  if (symbol.length !== 6) {
    return null;
  }

  const base = symbol.slice(0, 3);
  const quote = symbol.slice(3, 6);
  if (!FX_CURRENCIES.has(base) || !FX_CURRENCIES.has(quote) || base === quote) {
    return null;
  }

  return {
    assetType: "forex",
    tradeCurrency: quote,
    positionField: "lot_size",
    instrumentLabel: `${base}/${quote}`,
    lockAssetType: true,
    lockTradeCurrency: true,
    inferredFromSymbol: true,
  };
}

function buildCryptoInference(symbol) {
  for (const base of CRYPTO_BASES) {
    if (!symbol.startsWith(base)) {
      continue;
    }

    const remainder = symbol.slice(base.length);
    const matchedQuote = CRYPTO_QUOTES.find((quote) => remainder.startsWith(quote));
    if (!matchedQuote) {
      continue;
    }

    const brokerSuffix = remainder.slice(matchedQuote.length);
    if (!BROKER_SUFFIX_PATTERN.test(brokerSuffix)) {
      continue;
    }

    return {
      assetType: "crypto",
      tradeCurrency: normalizeTradeCurrency(matchedQuote),
      positionField: "lot_size",
      instrumentLabel: `${base}/${matchedQuote}`,
      lockAssetType: true,
      lockTradeCurrency: true,
      inferredFromSymbol: true,
    };
  }

  return null;
}

export function resolveTradeInstrumentInput({ asset, assetType, tradeCurrency }) {
  const symbol = normalizeAssetSymbol(asset);
  const normalizedAssetType = String(assetType || "forex").trim().toLowerCase();
  const known = KNOWN_SYMBOLS[symbol];

  if (known) {
    return {
      resolvedAssetType: known.assetType,
      inferredTradeCurrency: known.tradeCurrency,
      positionField: known.positionField,
      instrumentLabel: known.instrumentLabel,
      lockAssetType: true,
      lockTradeCurrency: true,
      inferredFromSymbol: true,
    };
  }

  const forexInference = buildForexInference(symbol);
  if (forexInference) {
    return {
      resolvedAssetType: forexInference.assetType,
      inferredTradeCurrency: forexInference.tradeCurrency,
      positionField: forexInference.positionField,
      instrumentLabel: forexInference.instrumentLabel,
      lockAssetType: true,
      lockTradeCurrency: true,
      inferredFromSymbol: true,
    };
  }

  const cryptoInference = buildCryptoInference(symbol);
  if (cryptoInference) {
    return {
      resolvedAssetType: cryptoInference.assetType,
      inferredTradeCurrency: cryptoInference.tradeCurrency,
      positionField: cryptoInference.positionField,
      instrumentLabel: cryptoInference.instrumentLabel,
      lockAssetType: true,
      lockTradeCurrency: true,
      inferredFromSymbol: true,
    };
  }

  return {
    resolvedAssetType: normalizedAssetType,
    inferredTradeCurrency: normalizeTradeCurrency(tradeCurrency) || "USD",
    positionField: ["forex", "metals", "indices", "commodities", "crypto"].includes(normalizedAssetType) ? "lot_size" : "quantity",
    instrumentLabel: symbol || "Custom instrument",
    lockAssetType: false,
    lockTradeCurrency: false,
    inferredFromSymbol: false,
  };
}